import { useMemo } from 'react'
import { ChainId } from '@nominex/nomiswap-sdk'
import { useAccount, useChainId, usePublicClient, useWalletClient, useConfig } from 'wagmi'
import { useAppKitState } from '@reown/appkit/react'
import { providers } from 'ethers'
import { type PublicClient, type WalletClient } from 'viem'

// Convert Viem PublicClient to ethers Provider
function publicClientToProvider(publicClient: PublicClient) {
  const { chain, transport } = publicClient
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  }
  if (transport.type === 'fallback')
    return new providers.FallbackProvider(
      (transport.transports as ReturnType<any>[]).map(
        ({ value }) => new providers.JsonRpcProvider(value?.url, network),
      ),
    )
  return new providers.JsonRpcProvider(transport.url, network)
}

// Convert Viem WalletClient to ethers Signer
function walletClientToSigner(walletClient: WalletClient) {
  const { account, chain, transport } = walletClient
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  }
  // Create a provider with a custom request method that adapts viem's transport
  const provider = new providers.Web3Provider(
    {
      request: async ({ method, params }) => {
        return transport.request({ method: method as any, params: params as any })
      },
    } as any,
    network,
  )
  const signer = provider.getSigner(account.address)
  return signer
}

const useWagmi = () => {
  const { address: account, connector, isConnected, chain } = useAccount()
  const { selectedNetworkId } = useAppKitState()
  const { data: walletClient } = useWalletClient()

  // Helper to parse CAIP chain ID (e.g., "eip155:56" -> 56)
  const parseChainId = (caipChainId: number | string | undefined): number => {
    if (!caipChainId) return ChainId.BSC

    // If it's already a number, return it
    if (typeof caipChainId === 'number') return caipChainId

    // Parse CAIP format: "eip155:56" -> 56
    const match = caipChainId.match(/eip155:(\d+)/)
    if (match) {
      return parseInt(match[1], 10)
    }

    // Try to parse as number string
    const parsed = parseInt(caipChainId, 10)
    return Number.isNaN(parsed) ? ChainId.BSC : parsed
  }

  // Determine the active chain ID
  let chainId
  if (isConnected) {
    chainId = chain ? chain.id : ChainId.BSC
  } else {
    // selectedNetworkId from AppKit can be in CAIP format
    chainId = parseChainId(selectedNetworkId) || ChainId.BSC
  }

  // Get the public client (Viem) for the current chain
  const publicClient = usePublicClient({ chainId })

  // Convert Viem clients to ethers providers/signers for backward compatibility
  const provider = useMemo(() => (publicClient ? publicClientToProvider(publicClient) : null), [publicClient])

  const signer = useMemo(() => (walletClient ? walletClientToSigner(walletClient) : null), [walletClient])

  return { account, chain, chainId, connector, isConnected, provider, signer }
}

export { useWagmi }
